/**
 * Haulage applicability for planning/sales views.
 *
 * A route is "haulage applicable" if ANY of:
 * 1. Vehicle type contains "HGV" (haulage-capable), OR
 * 2. Route category is "Haulage" or has haulage_collection/haulage_delivery stops, OR
 * 3. Meaningful empty legs (empty leg % of total route >= MIN_EMPTY_PCT_HAULAGE), OR
 * 4. Sizeable extended start time (earliestStart to first stop >= MIN_EXTENDED_MINUTES_HAULAGE within 12h window).
 */

/** Minimum empty leg % of total route distance to count as "meaningful" for haulage. */
export const MIN_EMPTY_PCT_HAULAGE = 10
/** Minimum extended sellable minutes (before first / after last) to count as haulage applicable. */
export const MIN_EXTENDED_MINUTES_HAULAGE = 60

type RouteLike = {
  category?: string | null
  vehicleType?: string | null
  vehicle_type?: string | null
  stops?: Array<{ slotType?: string; stop_type?: string }> | null
}

/** True if route's vehicle type name contains "HGV" (case-insensitive). */
export function isHgvRoute(route: RouteLike): boolean {
  const name = (route.vehicleType ?? route.vehicle_type ?? "").toLowerCase()
  return name.includes("hgv")
}

type RouteWithTimes = RouteLike & {
  plannedStartTime: Date
  plannedEndTime: Date
  stops?: Array<{
    plannedArrival: Date
    plannedDeparture: Date
    slotType?: string
    stop_type?: string
  }> | null
}

/**
 * Returns true if the route is designated as haulage or already has haulage stops.
 */
export function isHaulageDesignatedRoute(route: RouteLike): boolean {
  const category = (route.category ?? "").toLowerCase()
  if (category === "haulage") return true

  const stops = route.stops ?? []
  const hasHaulageStop = stops.some((s) => {
    const t = (s.slotType ?? s.stop_type ?? "").toLowerCase()
    return t === "haulage_collection" || t === "haulage_delivery"
  })
  return hasHaulageStop
}

/**
 * Stop types that are the operation moving itself around rather than serving a customer: they
 * carry no volume, so capacity, empty-leg and load-factor calculations all skip them.
 */
export const INTERNAL_STOP_TYPES = [
  "depot_injection",
  "direct_injection",
  "internal_stop",
  "charging_stop",
]

/** True for a stop type that moves the operation around rather than serving a customer. */
export function isInternalStopType(type: string | null | undefined): boolean {
  return INTERNAL_STOP_TYPES.includes((type ?? "").toLowerCase())
}

/** Either spelling of the type: a Stop and a PlannedStop carry `slotType`, a StopInstance `stop_type`. */
export function isCustomerStop(s: { slotType?: string; stop_type?: string }): boolean {
  return !isInternalStopType(s.slotType ?? s.stop_type)
}

/**
 * Extended sellable time: total route window = driverMaxHoursPerDay (e.g. 12h), not 12h on top of the route.
 * Route end = last stop's departure (any type), so return-to-depot / overnight end is included.
 * Earliest start = routeEnd − 12h (in absolute time). Overnight routes are factored in.
 * - extendedBeforeFirstMinutes = minutes from earliest start to first customer arrival, only when
 *   earliestStart < firstArrival (else 0).
 * - extendedAfterLastMinutes = 0.
 */
export function getExtendedSellableMinutes(
  route: RouteWithTimes,
  driverMaxHoursPerDay: number,
): { extendedBeforeFirstMinutes: number; extendedAfterLastMinutes: number } {
  const allStops = route.stops ?? []
  if (allStops.length === 0) {
    return { extendedBeforeFirstMinutes: 0, extendedAfterLastMinutes: 0 }
  }
  const customerStops = allStops.filter(isCustomerStop)
  if (customerStops.length === 0) {
    return { extendedBeforeFirstMinutes: 0, extendedAfterLastMinutes: 0 }
  }

  const lastStop = allStops[allStops.length - 1]
  const routeEndMs = new Date((lastStop as { plannedDeparture: Date }).plannedDeparture).getTime()
  const earliestStartMs = routeEndMs - driverMaxHoursPerDay * 60 * 60 * 1000
  const firstArrivalMs = new Date(
    (customerStops[0] as { plannedArrival: Date }).plannedArrival,
  ).getTime()

  const extendedBeforeFirstMinutes =
    earliestStartMs < firstArrivalMs
      ? Math.max(0, (firstArrivalMs - earliestStartMs) / (1000 * 60))
      : 0

  return { extendedBeforeFirstMinutes, extendedAfterLastMinutes: 0 }
}

/**
 * Full "haulage applicable" check:
 * - Vehicle type contains HGV, OR
 * - Designated haulage (category or haulage stops), OR
 * - Meaningful empty legs (emptyPct >= minEmptyPct), OR
 * - Sizeable extended start time (>= minExtendedMinutes).
 * minEmptyPct and minExtendedMinutes default to MIN_EMPTY_PCT_HAULAGE / MIN_EXTENDED_MINUTES_HAULAGE when not provided (e.g. from config).
 */
export function isHaulageApplicableRoute(
  route: RouteLike,
  options?: {
    hasEmptyLegs?: boolean
    emptyPct?: number | null
    extendedBeforeFirstMinutes?: number
    extendedAfterLastMinutes?: number
    /** Override: min empty leg % to count as meaningful (default MIN_EMPTY_PCT_HAULAGE). */
    minEmptyPct?: number
    /** Override: min extended sellable minutes (default MIN_EXTENDED_MINUTES_HAULAGE). */
    minExtendedMinutes?: number
  },
): boolean {
  const minEmptyPct = options?.minEmptyPct ?? MIN_EMPTY_PCT_HAULAGE
  const minExtendedMinutes = options?.minExtendedMinutes ?? MIN_EXTENDED_MINUTES_HAULAGE
  if (isHgvRoute(route)) return true
  if (isHaulageDesignatedRoute(route)) return true
  if (options?.hasEmptyLegs && (options?.emptyPct ?? 0) >= minEmptyPct) return true
  if ((options?.extendedBeforeFirstMinutes ?? 0) >= minExtendedMinutes) return true
  if ((options?.extendedAfterLastMinutes ?? 0) >= minExtendedMinutes) return true
  return false
}

